import { Box } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { FieldArray, FormikProvider, type FormikErrors, type FormikProps, type FormikTouched } from 'formik'
import { type FocusEvent } from 'react'
import { clsx } from '@mantine/styles'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'

import classes from '../LogVolunteeringActivityForm.module.scss'
import type { LogVolunteeringActivityFormFields } from '../../LogVolunteeringActivity.types'
import { parseTimeToHoursMinutes } from '../../parseTimeToHoursMinutes'
import { useOverlappingTimeLogs } from '../../hooks/useOverlappingTimeLogs'
import { toSimulatedTimeLogs } from '../../toSimulatedTimeLog'

import { Button, Feedback, FormField, Icon } from '@percent/lemonade'
import { DatePicker } from '@percent/workplace-giving/common/components/DatePicker/DatePicker'
import { useDateTimeFmt } from '@percent/workplace-giving/common/hooks'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { TimePicker } from '@percent/domain/giving'
import { SimulatedVolunteeringReward } from '@percent/workplace-giving/common/components/SimulatedVolunteeringReward/SimulatedVolunteeringReward'

type DateTimeFieldSetProps = Readonly<{
  values: LogVolunteeringActivityFormFields
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  touched: FormikTouched<LogVolunteeringActivityFormFields>
  errors: FormikErrors<LogVolunteeringActivityFormFields>
  handleBlur: <A = HTMLInputElement>(e: FocusEvent<A>) => void
  formikInstance: FormikProps<LogVolunteeringActivityFormFields>
  allowMultipleEntries: boolean
  duplicateDates?: string[]
  showOverlapWarning?: boolean
  showRewardPreview?: boolean
}>

type TimeLogErrors =
  | FormikErrors<{
      date?: Date
      startTime?: string
      endTime?: string
    }>[]
  | undefined

const computeDuration = (startTime?: string, endTime?: string): { hours: number; minutes: number } | null => {
  const start = parseTimeToHoursMinutes(startTime)
  const end = parseTimeToHoursMinutes(endTime)

  if (!start || !end) return null
  const diff = end.hours * 60 + end.minutes - (start.hours * 60 + start.minutes)

  if (diff <= 0) return null

  return { hours: Math.floor(diff / 60), minutes: diff % 60 }
}

export function DateTimeFieldSet({
  touched,
  errors,
  values,
  setFieldTouched,
  setFieldValue,
  handleBlur,
  formikInstance,
  allowMultipleEntries,
  duplicateDates,
  showOverlapWarning,
  showRewardPreview
}: DateTimeFieldSetProps) {
  const { t } = useTranslation()
  const isMobile = !!useMediaQuery('(max-width: 767px)')
  const { formatDateWODay } = useDateTimeFmt()
  const { timeLogs: timeLogsErrors } = errors
  const { hasOverlap } = useOverlappingTimeLogs(values.timeLogs)
  const noFutureTimeLogs = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_NO_FUTURE_TIME_LOGS)
  const maxDate = noFutureTimeLogs ? dayjs().endOf('day').toDate() : undefined

  return (
    <>
      <FormikProvider value={formikInstance}>
        <FieldArray name="timeLogs" validateOnChange>
          {({ push, remove }) => {
            const addAnotherFieldset = () => {
              push({ date: undefined, startTime: undefined, endTime: undefined })
            }

            const removeFieldset = (index: number) => {
              remove(index)
            }

            const isAddButtonDisabled = (index: number) => {
              const entry = values.timeLogs?.[index]
              const valuesAreFilled = !!entry?.date && !!entry?.startTime && !!entry?.endTime

              if (!valuesAreFilled) return true

              if (!computeDuration(entry.startTime, entry.endTime)) return true

              const entryTouched = touched.timeLogs?.[index]

              return !!entryTouched && timeLogsErrors?.[index] !== undefined
            }

            return (
              <>
                {values.timeLogs.map((entry, index) => {
                  const entryErrors = (timeLogsErrors as TimeLogErrors)?.[index]
                  const entryTouched = touched.timeLogs?.[index]

                  const isFirstRow = index === 0
                  const isLastRow = index === values.timeLogs.length - 1
                  const showRemoveBtn = !isFirstRow
                  const showDesktopAddBtn = isLastRow && !isMobile && allowMultipleEntries
                  const showMobileAddBtn = isLastRow && isMobile && allowMultipleEntries
                  const rowLevelError =
                    typeof timeLogsErrors?.[index] === 'string' ? (timeLogsErrors?.[index] as string) : null
                  const firstFieldError =
                    (entryTouched?.date && entryErrors?.date) ||
                    (entryTouched?.startTime && entryErrors?.startTime) ||
                    (entryTouched?.endTime && entryErrors?.endTime) ||
                    null
                  const rowErrorMessage = rowLevelError || firstFieldError

                  return (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className={clsx(classes.TimeFieldsWrapper, {
                        [classes.MultipleRows]: values.timeLogs.length > 1,
                        [classes.LastRow]: isLastRow
                      })}
                    >
                      <Box className={classes.InputsWrapper}>
                        <Box className={classes.DateFieldItem} data-testid={`dateField-${index}`}>
                          <FormField
                            label={index === 0 ? t('workplace_giving.volunteering.activityForm.date.label') : undefined}
                            necessity="required"
                            status={entryTouched?.date && entryErrors?.date ? 'danger' : 'default'}
                          >
                            <DatePicker
                              fieldName={`timeLogs[${index}].date`}
                              placeholder={formatDateWODay(dayjs().add(1, 'year').startOf('year').toDate())}
                              value={entry.date}
                              handleBlur={handleBlur}
                              handleChange={async date => {
                                setFieldTouched(`timeLogs[${index}].date`)
                                setFieldValue(`timeLogs[${index}].date`, date)
                              }}
                              error={!!entryErrors?.date && !!entryTouched?.date}
                              maxDate={maxDate}
                              maxDateTooltip={
                                noFutureTimeLogs
                                  ? t('workplace_giving.volunteering.activityForm.futureDateDisabled')
                                  : undefined
                              }
                            />
                          </FormField>
                        </Box>

                        <Box className={classes.TimeFieldItem} data-testid={`startTimeField-${index}`}>
                          <FormField
                            label={
                              index === 0 ? t('workplace_giving.volunteering.activityForm.startTime.label') : undefined
                            }
                            necessity="required"
                            status={entryTouched?.startTime && entryErrors?.startTime ? 'danger' : 'default'}
                          >
                            <TimePicker
                              fieldName={`timeLogs[${index}].startTime`}
                              value={entry.startTime}
                              handleBlur={handleBlur}
                              handleChange={time => {
                                setFieldValue(`timeLogs[${index}].startTime`, time)
                                setFieldTouched(`timeLogs[${index}].startTime`)
                              }}
                              error={!!entryErrors?.startTime && !!entryTouched?.startTime}
                              inputPlaceholder={t('workplace_giving.volunteering.activityForm.startTime.placeholder')}
                            />
                          </FormField>
                        </Box>

                        <Box className={classes.TimeFieldItem} data-testid={`endTimeField-${index}`}>
                          <FormField
                            label={
                              index === 0 ? t('workplace_giving.volunteering.activityForm.endTime.label') : undefined
                            }
                            necessity="required"
                            status={entryTouched?.endTime && entryErrors?.endTime ? 'danger' : 'default'}
                          >
                            <TimePicker
                              fieldName={`timeLogs[${index}].endTime`}
                              value={entry.endTime}
                              handleBlur={handleBlur}
                              handleChange={time => {
                                setFieldValue(`timeLogs[${index}].endTime`, time)
                                setFieldTouched(`timeLogs[${index}].endTime`)
                              }}
                              error={!!entryErrors?.endTime && !!entryTouched?.endTime}
                              inputPlaceholder={t('workplace_giving.volunteering.activityForm.endTime.placeholder')}
                            />
                          </FormField>
                        </Box>

                        <Box className={classes.RowActions}>
                          {showRemoveBtn ? (
                            <button
                              type="button"
                              className={classes.RemoveEntryBtn}
                              aria-label={t('workplace_giving.common.remove')}
                              onClick={() => removeFieldset(index)}
                            >
                              <Icon size={6} color="gray400" name="bin" />
                            </button>
                          ) : null}

                          {showDesktopAddBtn ? (
                            <Button
                              data-testid="addAnotherBtn-desktop"
                              size="small"
                              variant="secondary"
                              shape="oval"
                              onClick={addAnotherFieldset}
                              disabled={isAddButtonDisabled(index)}
                            >
                              + {t('workplace_giving.volunteering.activityForm.addAnother')}
                            </Button>
                          ) : null}
                        </Box>
                      </Box>

                      {rowErrorMessage ? (
                        <Feedback variant="critical" align="start">
                          {rowErrorMessage}
                        </Feedback>
                      ) : null}

                      {showMobileAddBtn ? (
                        <Box className={classes.AddBtnWrapper}>
                          <Button
                            data-testid="addAnotherBtn-mobile"
                            size="small"
                            variant="secondary"
                            stretch
                            onClick={addAnotherFieldset}
                            disabled={isAddButtonDisabled(index)}
                          >
                            + {t('workplace_giving.volunteering.activityForm.addAnother')}
                          </Button>
                        </Box>
                      ) : null}
                    </Box>
                  )
                })}
                <Box>
                  {showOverlapWarning ? (
                    <Feedback variant="warning" align="start">
                      {t('workplace_giving.volunteering.activityForm.timeOverlapDetected')}
                    </Feedback>
                  ) : null}
                  {!showOverlapWarning && hasOverlap ? (
                    <Feedback variant="warning" align="start">
                      {t('workplace_giving.volunteering.activityForm.timeOverlapWarning')}
                    </Feedback>
                  ) : null}
                  {!showOverlapWarning && !hasOverlap ? (
                    <Feedback variant="default" align="start">
                      {t('workplace_giving.volunteering.activityForm.timeHelperText')}
                    </Feedback>
                  ) : null}
                </Box>
                {showRewardPreview ? (
                  <SimulatedVolunteeringReward timeLogs={toSimulatedTimeLogs(values)} variant="highlighted" />
                ) : null}
              </>
            )
          }}
        </FieldArray>
      </FormikProvider>
      {duplicateDates?.length ? (
        <Box style={{ marginTop: '0.5rem' }}>
          <Feedback variant="critical">
            {t('workplace_giving.validation.duplicateDate', {
              dates: duplicateDates.map(date => `${formatDateWODay(new Date(date))}`).join(', ')
            })}
          </Feedback>
        </Box>
      ) : null}
    </>
  )
}
